import { Pause, Play } from '@phosphor-icons/react';
import { Button, IconButton, Inline, Slider, Stack } from '@reelplicate/ui';
import { type ReactNode, useState } from 'react';

import { useMediaQuery } from '@/lib/use-media-query';

import { PreviewStage } from './preview-stage';
import { TimelinePanel } from './timeline-panel';
import { formatTime } from './timeline-state';
import type { Playback } from './use-playback';
import type { ReelEditor } from './use-reel-editor';

/**
 * The reel editor's stage: the preview, and under it the timeline - or, on
 * a phone, a play button and a trim bar.
 */
export function ReelStage({
  editor,
  playback,
}: {
  editor: ReelEditor;
  playback: Playback;
}) {
  const wide = useMediaQuery('(min-width: 48rem)');
  const { saved, state, dispatch } = editor;
  if (!saved) return null;

  const actions = (
    <>
      {editor.rendering ? (
        <span role="status" className="text-caption text-text-secondary">
          Rendering version {editor.pendingVersion?.version}…
        </span>
      ) : (
        editor.dirty && (
          <>
            {editor.problem && (
              <span role="alert" className="text-caption text-status-danger-fg">
                {editor.problem}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={() => editor.discard()}>
              Discard
            </Button>
          </>
        )
      )}
      <Button
        size="sm"
        disabled={
          !editor.dirty || Boolean(editor.problem) || editor.rendering || editor.saving
        }
        loading={editor.saving}
        onClick={() => void editor.save()}
      >
        Save edit
      </Button>
    </>
  );

  return (
    <Stack gap={3} className="min-h-0 flex-1">
      <div className="bg-bg-subtle flex min-h-0 flex-1 items-center justify-center rounded-lg p-3">
        <PreviewStage
          sourceUrl={saved.sourceUrl}
          width={saved.width}
          height={saved.height}
          timeline={state.timeline}
          words={saved.words}
          playback={playback}
          className="max-h-full max-w-full"
        />
      </div>
      {wide ? (
        <TimelinePanel
          timeline={state.timeline}
          selected={state.selected}
          canUndo={state.past.length > 0}
          canRedo={state.future.length > 0}
          words={saved.words}
          playback={playback}
          dispatch={dispatch}
          onAddText={() => editor.addText(playback.time)}
          actions={actions}
        />
      ) : (
        <MobileTrim editor={editor} playback={playback} actions={actions} />
      )}
    </Stack>
  );
}

/**
 * On a phone: play, and trim the start and end with one bar. Cuts made on a
 * bigger screen are kept; trimming them is for there.
 */
function MobileTrim({
  editor,
  playback,
  actions,
}: {
  editor: ReelEditor;
  playback: Playback;
  actions: ReactNode;
}) {
  const { state, dispatch } = editor;
  const [clip] = state.timeline.clips;
  const single = state.timeline.clips.length === 1 && clip;
  const [dragging, setDragging] = useState<number[] | null>(null);
  const range = dragging ?? (single ? [clip.in, clip.out] : [0, 0]);

  return (
    <Stack gap={3}>
      <Inline gap={2} alignY="center">
        <IconButton
          icon={playback.playing ? Pause : Play}
          variant="outline"
          aria-label={playback.playing ? 'Pause' : 'Play'}
          onClick={playback.toggle}
        />
        <span className="text-caption text-text-secondary tabular-nums">
          {formatTime(playback.time)} / {formatTime(playback.duration)}
        </span>
      </Inline>
      {single ? (
        <Stack gap={1}>
          <span className="text-label-sm text-text-primary">Trim</span>
          <Slider
            aria-label="Trim"
            thumbLabels={['Start', 'End']}
            min={0}
            max={state.sourceDuration}
            step={0.1}
            minStepsBetweenThumbs={3}
            value={range}
            formatValue={formatTime}
            onValueChange={setDragging}
            onValueCommit={([from, to]) => {
              setDragging(null);
              if (from === undefined || to === undefined) return;
              dispatch({ type: 'checkpoint' });
              dispatch({ type: 'trim', id: clip.id, edge: 'out', to, transient: true });
              dispatch({
                type: 'trim',
                id: clip.id,
                edge: 'in',
                to: from,
                transient: true,
              });
            }}
          />
        </Stack>
      ) : (
        <p className="text-body-sm text-text-secondary">
          This reel has {state.timeline.clips.length} cuts - change them on a bigger
          screen.
        </p>
      )}
      <Inline gap={2} alignY="center" align="right">
        {actions}
      </Inline>
    </Stack>
  );
}
